/**
 * Validação dos campos que têm formato fixo no Brasil.
 *
 * Papel: além dos formatos de documento e de contato, aqui ficam os dois
 * schemas de dinheiro — `dinheiroSchema`, de duas casas, e
 * `valorPorUnidadeSchema`, de quatro — e as variantes opcionais de cada um. Aqui
 * ficam os schemas de CPF, placa, CEP, renavam, chassi, cartão do
 * SUS, código FIPE, unidade federativa, dinheiro e competência mensal — cada um
 * devolvendo o valor já em forma canônica, sem a pontuação com que foi digitado.
 *
 * Como usar: um campo com formato entra no schema do módulo por um destes, e
 * nunca por `z.string()` com um `regex` escrito na hora. O que estes schemas
 * devolvem é o que vai para o banco: `cpfSchema` guarda `12345678909`, e o
 * `123.456.789-09` que a pessoa vê é produzido por `formatarCpf`, em
 * `apps/web/src/shared/lib/formato.ts`, na hora de desenhar a tela. Repetir a
 * validação no módulo é o caminho para as duas pontas divergirem no dia em que
 * uma das cópias for corrigida e a outra não.
 *
 * Todos aceitam o valor pontuado, porque é assim que o dado chega: colado de uma
 * conversa, digitado com máscara ou importado de planilha, cada fonte com a sua
 * grafia. A limpeza mora aqui, e não no componente de entrada, porque quem
 * chamar a API direto passa por cima do componente mas não passa por cima do
 * schema.
 */
import { z } from 'zod';

/**
 * Descarta tudo o que não for dígito.
 *
 * @param valor - Texto como foi digitado, colado ou importado.
 * @returns Somente os dígitos, na ordem em que apareciam.
 */
export function apenasDigitos(valor: string): string {
  return valor.replace(/\D/g, '');
}

/**
 * Confere os dois dígitos verificadores de um CPF.
 *
 * Verificar só o comprimento aceitaria `11111111111` e qualquer sequência de
 * onze dígitos digitada por engano, que é justamente o erro que aparece quando
 * alguém preenche um cadastro às pressas. Os repetidos são recusados antes do
 * cálculo porque satisfazem a fórmula apesar de não serem CPF de ninguém.
 *
 * @param digitos - Exatamente onze dígitos, sem pontuação.
 * @returns Verdadeiro quando os dois verificadores conferem.
 */
export function cpfEhValido(digitos: string): boolean {
  if (digitos.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digitos)) return false;

  for (const [posicao, pesoInicial] of [
    [9, 10],
    [10, 11],
  ] as const) {
    let soma = 0;
    for (let i = 0; i < posicao; i += 1) {
      soma += Number(digitos[i]) * (pesoInicial - i);
    }
    const resto = (soma * 10) % 11;
    const verificador = resto === 10 ? 0 : resto;
    if (verificador !== Number(digitos[posicao])) return false;
  }

  return true;
}

/**
 * CPF obrigatório, guardado como onze dígitos.
 *
 * Aceita a forma pontuada `123.456.789-09` e a forma sem pontuação. Um CPF com
 * verificador errado é recusado, e não guardado para correção posterior: o CPF
 * é a chave que casa uma pessoa entre módulos, e um dígito trocado criaria duas
 * pessoas onde existe uma.
 */
export const cpfSchema = z
  .string()
  .trim()
  .transform(apenasDigitos)
  .refine(cpfEhValido, { message: 'CPF inválido.' });

/**
 * CPF opcional, com a mesma verificação de dígitos quando preenchido.
 *
 * Existe para cadastros que misturam pessoa física e jurídica, como o de
 * favorecidos: exigir CPF impediria cadastrar um fornecedor com CNPJ.
 */
export const cpfOpcionalSchema = z
  .string()
  .trim()
  .nullish()
  .transform((valor) => {
    if (valor == null) return null;
    const digitos = apenasDigitos(valor);
    return digitos === '' ? null : digitos;
  })
  .refine((valor) => valor === null || cpfEhValido(valor), { message: 'CPF inválido.' });

/**
 * Placa de veículo, em maiúsculas e sem hífen.
 *
 * Aceita os dois padrões: o Mercosul (`ABC1D23`, letra na quinta posição) e o
 * antigo (`ABC1234`). Carros emplacados antes da troca continuam rodando com a
 * placa antiga, e recusar esse formato tornaria impossível editar o cadastro de
 * um deles.
 */
export const placaSchema = z
  .string()
  .trim()
  .transform((valor) => valor.toUpperCase().replace(/[^A-Z0-9]/g, ''))
  .refine((valor) => /^[A-Z]{3}\d[A-Z]\d{2}$/.test(valor) || /^[A-Z]{3}\d{4}$/.test(valor), {
    message: 'Placa inválida (esperado ABC1D23 ou ABC1234).',
  });

/**
 * CEP opcional, guardado como oito dígitos.
 */
export const cepOpcionalSchema = z
  .string()
  .trim()
  .nullish()
  .transform((valor) => {
    if (valor == null) return null;
    const digitos = apenasDigitos(valor);
    return digitos === '' ? null : digitos;
  })
  .refine((valor) => valor === null || valor.length === 8, {
    message: 'CEP deve ter 8 dígitos.',
  });

/**
 * Renavam opcional, guardado como onze dígitos.
 *
 * O documento admite zeros à esquerda, como em `00123456789`, e por isso o
 * campo é texto, nunca número: convertido para número, o zero inicial
 * desaparece e o valor deixa de casar com o documento do carro.
 */
export const renavamOpcionalSchema = z
  .string()
  .trim()
  .nullish()
  .transform((valor) => {
    if (valor == null) return null;
    const digitos = apenasDigitos(valor);
    return digitos === '' ? null : digitos;
  })
  .refine((valor) => valor === null || valor.length === 11, {
    message: 'Renavam deve ter 11 dígitos.',
  });

/**
 * Chassi opcional, em maiúsculas, com dezessete caracteres.
 *
 * As letras `I`, `O` e `Q` não existem em chassi, justamente para não serem
 * confundidas com `1` e `0`. Recusá-las aqui transforma um erro de digitação
 * silencioso — que só apareceria anos depois, ao consultar o veículo — em uma
 * mensagem na hora do cadastro.
 */
export const chassiOpcionalSchema = z
  .string()
  .trim()
  .nullish()
  .transform((valor) => {
    if (valor == null) return null;
    const limpo = valor.toUpperCase().replace(/[^A-Z0-9]/g, '');
    return limpo === '' ? null : limpo;
  })
  .refine((valor) => valor === null || /^[A-HJ-NPR-Z0-9]{17}$/.test(valor), {
    message: 'Chassi deve ter 17 caracteres, sem as letras I, O e Q.',
  });

/**
 * Cartão Nacional de Saúde opcional, guardado como quinze dígitos.
 */
export const cnsOpcionalSchema = z
  .string()
  .trim()
  .nullish()
  .transform((valor) => {
    if (valor == null) return null;
    const digitos = apenasDigitos(valor);
    return digitos === '' ? null : digitos;
  })
  .refine((valor) => valor === null || valor.length === 15, {
    message: 'Cartão Nacional de Saúde deve ter 15 dígitos.',
  });

/**
 * Converte o texto de um campo de dinheiro em número.
 *
 * Com vírgula presente, a leitura é brasileira: a vírgula é decimal e todo ponto
 * é de milhar — `1.234.567,89` vale um milhão e pouco. Sem vírgula, dois ou mais
 * pontos também são de milhar (`R$ 1.234.567`), mas um ponto só é decimal
 * (`2345.6`). Esse último caso é ambíguo, e a leitura decimal é a que não
 * multiplica valores: `92.387` lido como milhar viraria noventa e dois mil, e o
 * `CampoMascarado` nunca produz essa forma, porque sempre escreve a vírgula — ela
 * só chega de dado importado, onde o ponto é decimal.
 *
 * @param valor - Texto do campo, com ou sem `R$`, pontos, vírgula e sinal.
 * @returns O número correspondente, ou `null` quando não há dígito algum ou o
 *   texto não forma um número.
 */
export function converterDinheiro(valor: string): number | null {
  const limpo = valor.replace(/[^\d,.-]/g, '');
  if (limpo === '' || !/\d/.test(limpo)) return null;

  const pontos = (limpo.match(/\./g) ?? []).length;
  const ultimaVirgula = limpo.lastIndexOf(',');
  const ultimoPonto = limpo.lastIndexOf('.');

  let normalizado: string;
  if (ultimaVirgula > ultimoPonto) {
    normalizado = limpo.replace(/\./g, '').replace(',', '.');
  } else if (ultimaVirgula === -1 && pontos > 1) {
    normalizado = limpo.replace(/\./g, '');
  } else {
    normalizado = limpo.replace(/,/g, '');
  }

  const numero = Number(normalizado);
  return Number.isFinite(numero) ? numero : null;
}

/**
 * Arredonda para centavos, com metade para longe do zero.
 *
 * Multiplicar por cem e arredondar não basta: `1.005` é guardado em binário como
 * `1.00499…`, e `Math.round(1.005 * 100)` dá `100`, perdendo o centavo. Deslocar
 * a vírgula pelo expoente do texto — `1.005e2` — faz a conta sobre o valor
 * decimal que foi escrito, e não sobre a aproximação binária dele. Um centavo
 * perdido por lançamento é o que faz o total de um mês não fechar com a soma das
 * linhas.
 *
 * @param valor - Número finito, de qualquer precisão.
 * @returns O valor com no máximo duas casas decimais.
 */
export function arredondarCentavos(valor: number): number {
  const absoluto = Math.abs(valor);
  const texto = absoluto.toPrecision(15);
  // Notação científica só aparece em valores fora de qualquer faixa monetária
  // (abaixo de um milionésimo ou acima de 10¹⁵), onde o ruído binário não pesa.
  const centavos = texto.includes('e')
    ? Math.round(absoluto * 100)
    : Math.round(Number(`${texto}e2`));
  if (centavos === 0) return 0;
  return (Math.sign(valor) * centavos) / 100;
}

/**
 * Valor em reais, com duas casas decimais.
 *
 * Aceita número e também o texto que sai de um campo com máscara, porque o
 * formulário entrega `"R$ 1.234,56"` e a API pode receber `1234.56` de quem a
 * chama direto. Arredonda para duas casas por `arredondarCentavos`: a coluna é
 * `numeric(12,2)`, e deixar a terceira casa chegar ao banco produz um total que
 * não fecha com a soma das parcelas mostradas na tela.
 */
export const dinheiroSchema = z
  .union([z.number(), z.string()])
  .transform((valor) => (typeof valor === 'number' ? valor : converterDinheiro(valor)))
  .refine((valor): valor is number => valor !== null && Number.isFinite(valor), {
    message: 'Valor inválido.',
  })
  .transform(arredondarCentavos);

/**
 * Lê como número o que um campo de dinheiro tem dentro, para uma soma ao vivo.
 *
 * Quem chama são os formulários que mostram um total enquanto a pessoa digita, e
 * o valor que chega a eles tem duas procedências: o texto com máscara que a
 * pessoa acabou de escrever, como `"R$ 1.234,56"`, e o `String(valor)` com que o
 * formulário semeia um registro já gravado, como `"2450.75"`.
 *
 * Tratar o ponto como separador de milhar acerta o primeiro e destrói o segundo:
 * `"2450.75"` viraria `245075`, e o total de um orçamento recém-aberto sairia
 * cem vezes maior até alguém tocar no campo. Quem decide qual é qual é
 * `converterDinheiro`, pela posição relativa do último ponto e da última
 * vírgula.
 *
 * @param valor - O conteúdo do campo, número ou texto.
 * @returns O número, ou zero quando o campo está vazio ou não tem dígito algum —
 *   um total ao vivo não tem onde mostrar erro, e travá-lo em `NaN` esconderia o
 *   que as outras linhas somam.
 */
export function dinheiroDoCampo(valor: unknown): number {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
  if (typeof valor !== 'string') return 0;
  return converterDinheiro(valor) ?? 0;
}

/**
 * Valor em reais que vale zero quando não vem preenchido.
 *
 * **Não é `dinheiroSchema.default(0)`.** O `default` do Zod só entra quando a
 * chave chega `undefined`, e o campo que a pessoa não tocou chega como string
 * vazia — que `converterDinheiro` devolve como `null` e o `refine` recusa com
 * "Valor inválido.". O desconto em branco de um formulário recém-aberto seria
 * barrado num campo que ninguém preencheu, e a mensagem apontaria para ele.
 *
 * Aqui vazio, nulo e ausente valem todos zero, e é isso que distingue este
 * schema de `dinheiroOpcionalSchema`: lá o campo em branco significa "não há
 * valor" e vira `null`; aqui significa "nada a abater" e vira `0`.
 */
export const dinheiroComPadraoZeroSchema = z
  .union([z.number(), z.string()])
  .nullish()
  .transform((valor) => {
    if (valor == null) return 0;
    if (typeof valor === 'number') return valor;
    return valor.trim() === '' ? 0 : converterDinheiro(valor);
  })
  .refine((valor): valor is number => valor !== null && Number.isFinite(valor), {
    message: 'Valor inválido.',
  })
  .transform(arredondarCentavos);

/**
 * Valor por unidade, com até quatro casas decimais.
 *
 * **Não é `dinheiroSchema`**, e a diferença importa: dinheiro tem duas casas
 * porque é o que se paga, e valor por unidade sai de uma divisão. Um pacote de
 * três unidades que custou `10,00` vale `3,3333` cada; uma diária apurada a
 * partir de um salário mensal cai na mesma conta. Arredondar para centavos faz o
 * produto deixar de fechar com o que foi gasto.
 *
 * Aceita número e o texto que sai de um campo com máscara, como
 * `dinheiroSchema`. A coluna correspondente é sempre `numeric(_, 4)`.
 */
export const valorPorUnidadeSchema = z
  .union([z.number(), z.string()])
  .transform((valor) => {
    if (typeof valor === 'number') return valor;
    const texto = valor.trim().replace(/[R$\s]/g, '');
    if (texto === '') return Number.NaN;
    // Em formato brasileiro o ponto separa milhar e a vírgula separa decimal.
    return Number(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto);
  })
  .refine((valor) => Number.isFinite(valor), { message: 'Valor inválido.' })
  .transform((valor) => Math.round(valor * 10_000) / 10_000);

/**
 * Valor por unidade opcional, que aceita campo vazio e devolve `null`.
 *
 * A forma é `.nullish()` depois do `union`, pelo mesmo motivo de
 * `dinheiroOpcionalSchema`: um `union` com `z.undefined()` não torna a chave
 * omitível no Zod v4.
 */
export const valorPorUnidadeOpcionalSchema = z
  .union([z.number(), z.string()])
  .nullish()
  .transform((valor) => {
    if (valor == null || valor === '') return null;
    if (typeof valor === 'number') return valor;
    const texto = valor.trim().replace(/[R$\s]/g, '');
    if (texto === '') return null;
    return Number(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto);
  })
  .refine((valor) => valor === null || Number.isFinite(valor), { message: 'Valor inválido.' })
  .transform((valor) => (valor === null ? null : Math.round(valor * 10_000) / 10_000));

/** Valor em reais opcional, que aceita campo vazio e devolve `null`.
 *
 * A forma é `.nullish()` depois do `union`, e não `z.null()` e `z.undefined()`
 * dentro dele: um `union` com `z.undefined()` **não** torna a chave omitível no
 * Zod v4, e o corpo que simplesmente não trouxesse o campo seria recusado com
 * `expected nonoptional` — um 400 onde a resposta certa seria gravar `null`.
 */
export const dinheiroOpcionalSchema = z
  .union([z.number(), z.string()])
  .nullish()
  .transform((valor) => {
    if (valor == null || valor === '') return null;
    return typeof valor === 'number' ? valor : converterDinheiro(valor);
  })
  .refine((valor) => valor === null || Number.isFinite(valor), { message: 'Valor inválido.' })
  .transform((valor) => (valor === null ? null : arredondarCentavos(valor)));

/**
 * Competência mensal, no formato `AAAA-MM`.
 *
 * Existe porque o banco de horas e o fechamento financeiro são organizados por
 * mês inteiro, não por dia. No banco a competência vira o primeiro dia do mês,
 * em coluna `date`, o que mantém a ordenação correta e dispensa comparar texto.
 */
export const competenciaSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, { message: 'Competência inválida (esperado AAAA-MM).' });

/**
 * Traduz `AAAA-MM` para o primeiro dia do mês, em ISO.
 *
 * @param competencia - Competência já validada por `competenciaSchema`.
 * @returns Data ISO do dia 1, pronta para uma coluna `date`.
 */
export function competenciaParaData(competencia: string): string {
  return `${competencia}-01`;
}

/**
 * Traduz uma data ISO na competência do mês a que ela pertence.
 *
 * @param data - Data ISO `AAAA-MM-DD`, de qualquer dia do mês.
 * @returns Os sete primeiros caracteres, que são a competência.
 */
export function dataParaCompetencia(data: string): string {
  return data.slice(0, 7);
}

/**
 * Código FIPE de um modelo de veículo, opcional.
 *
 * São seis dígitos, um hífen e mais um — `123456-7` —, e é o que identifica o
 * modelo na tabela que dá o valor de mercado do carro. O hífen faz parte do
 * código e é preservado: ao contrário do telefone e do CPF, aqui não há forma
 * canônica sem pontuação a extrair.
 *
 * Aceita o código digitado sem o hífen e o repõe, porque quem copia da consulta
 * costuma trazer só os dígitos.
 */
export const fipeOpcionalSchema = z
  .string()
  .trim()
  .nullish()
  .transform((valor) => {
    if (valor == null) return null;
    const digitos = apenasDigitos(valor);
    if (digitos === '') return null;
    return digitos.length === 7 ? `${digitos.slice(0, 6)}-${digitos.slice(6)}` : valor.trim();
  })
  .refine((valor) => valor === null || /^\d{6}-\d$/.test(valor), {
    message: 'Código FIPE inválido (esperado 000000-0).',
  });

/**
 * As siglas das unidades federativas, em ordem alfabética.
 *
 * São vinte e sete e não mudam desde 1988, o que as torna lista fechada segura
 * — ao contrário do nome de uma cidade, que é texto livre porque muda por lei
 * municipal e tem grafia disputada.
 */
export const UNIDADES_FEDERATIVAS = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
] as const;

/**
 * Unidade federativa, guardada em duas letras maiúsculas.
 *
 * Aceita a sigla em qualquer caixa — `sp` digitado depressa vira `SP` — e
 * recusa o que não estiver em `UNIDADES_FEDERATIVAS`. Conferir só o comprimento
 * deixaria passar `XX` e `PP`, e o erro apareceria meses depois, num
 * agrupamento por estado que traz uma linha a mais que ninguém sabe de onde
 * veio.
 */
export const ufSchema = z
  .string()
  .trim()
  .toUpperCase()
  .pipe(z.enum(UNIDADES_FEDERATIVAS, { message: 'UF inválida.' }));

export type UnidadeFederativa = (typeof UNIDADES_FEDERATIVAS)[number];
